import { classifyAvailability } from './taskAvailability';
import type {
  TaskProgress,
  TaskProgressDoc,
  ScheduleTask,
  ProjectedTask,
} from '../types/ScheduleTask';
import type { Activity } from '@castoredc/castor_prose';
import type { DbTask } from '@hub/common';

const mapProgress = (status: DbTask['status'] | undefined): TaskProgress => {
  switch (status) {
    case 'completed':
      return 'done';
    case 'touched':
    case 'submitting':
    case 'post-questionnaire':
      return 'in-progress';
    case 'new':
    case undefined:
      return 'new';
  }
};

// Each activity contributes exactly one projected task (the schedule DSL declares
// `times: 1`). Activities with no task or a non-questionnaire kind are skipped.
// Recurring activities are out of scope.
// Computed from PROSE alone — availability needs no tasks subscription.
export const projectTasks = (
  activities: Activity[],
  now: Date
): ProjectedTask[] =>
  activities.flatMap(activity => {
    const task = activity.tasks[0];
    const kind = activity.kind?.kind;
    if (
      !task?.beginning ||
      !task.end ||
      kind?.$case !== 'questionnaireActivity'
    ) {
      return [];
    }
    const beginning = new Date(task.beginning.seconds * 1000);
    const end = new Date(task.end.seconds * 1000);
    return [
      {
        activityName: activity.name,
        questionnaireName: kind.value.questionnaire,
        beginning,
        end,
        availability: classifyAvailability(now, beginning, end),
        autoPresent: task.autoPresent,
      },
    ];
  });

// Joins each projected task's task document to add progress — the only step that
// needs the tasks subscription. A projected task with no task document is `new`.
export const attachProgress = (
  projectedTasks: ProjectedTask[],
  taskDocs: TaskProgressDoc[]
): ScheduleTask[] => {
  const docsByActivity = new Map(taskDocs.map(doc => [doc.activityName, doc]));
  return projectedTasks.map(task => ({
    ...task,
    progress: mapProgress(docsByActivity.get(task.activityName)?.status),
  }));
};
